import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Link,
  Button,
  Stack,
  CircularProgress,
  Paper,
  Container,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { RootState } from '../../app/store';
import { CommentPriority, CommentSentiment, CommentCategory } from '../../types/comments';
import * as tiktokService from '../../services/api/tiktok.service';
import { TikTokComment } from '../../services/api/tiktok.service';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

interface Filters {
  priority: string;
  sentiment: string;
  category: string;
  is_replied: string;
  brand_id: string;
  search: string;
}

interface FilterOptions {
  categories: string[];
  sentiments: string[];
  priorities: number[];
  brands: Array<{ brand_id: string; brand_name: string; }>;
}

const convertUrlsToLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: 'break-word' }}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

const TikTokCommentsPage: React.FC = () => {
  const theme = useTheme();
  const [comments, setComments] = useState<TikTokComment[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    categories: [],
    sentiments: [],
    priorities: [],
    brands: []
  });
  const [filters, setFilters] = useState<Filters>({
    priority: '',
    sentiment: '',
    category: '',
    is_replied: '',
    brand_id: '',
    search: ''
  });
  const [editingComment, setEditingComment] = useState<TikTokComment | null>(null);
  const [editData, setEditData] = useState<Partial<TikTokComment>>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch filter options when component mounts
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const data = await tiktokService.getTikTokCommentFilterOptions();
        setFilterOptions({
          categories: data.categories || [],
          sentiments: data.sentiments || [],
          priorities: data.priorities || [1, 2, 3],
          brands: data.brands || []
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  // Fetch comments when filters change or refresh
  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      try {
        const filtersToSend: tiktokService.TikTokCommentFilters = {
          page,
          limit: rowsPerPage,
          search: filters.search || undefined,
        };

        if (filters.priority) filtersToSend.priority = parseInt(filters.priority);
        if (filters.sentiment) filtersToSend.sentiment = filters.sentiment;
        if (filters.category) filtersToSend.category = filters.category;
        if (filters.is_replied) filtersToSend.is_replied = filters.is_replied === 'true';
        if (filters.brand_id) filtersToSend.brand_id = filters.brand_id;

        const response = await tiktokService.getTikTokComments(filtersToSend);
        setComments(response.data || []);
        setTotal(response.total || 0);
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [page, rowsPerPage, filters, refreshKey]);

  // Handle filter changes
  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilters({ ...filters, [event.target.name]: event.target.value });
    setPage(1);
  };

  // Handle search changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: event.target.value });
    setPage(1);
  };

  // Get priority label based on priority value
  const getPriorityLabel = (priority: number): string => {
    switch (priority) {
      case 1:
        return 'High';
      case 2:
        return 'Medium';
      case 3:
        return 'Low';
      default:
        return 'Unknown';
    }
  };

  // Get color for priority chip
  const getPriorityColor = (priority: number): ChipColor => {
    switch (priority) {
      case 1:
        return 'error';
      case 2:
        return 'warning';
      case 3:
        return 'info';
      default:
        return 'default';
    }
  };

  // Get color for sentiment chip
  const getSentimentColor = (sentiment: string | null): ChipColor => {
    switch (sentiment) {
      case 'positive':
        return 'success';
      case 'negative':
        return 'error';
      case 'neutral':
        return 'info';
      default:
        return 'default';
    }
  };

  // Start editing a comment
  const handleEditStart = (comment: TikTokComment) => {
    setEditingComment(comment);
    setEditData({
      sentiment: comment.sentiment,
      category: comment.category,
      priority: comment.priority,
      suggested_reply: comment.suggested_reply
    });
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingComment(null);
    setEditData({});
  };

  // Save edited comment
  const handleEditSave = async () => {
    if (!editingComment) return;

    try {
      await tiktokService.updateTikTokComment(editingComment.id, editData);
      setEditingComment(null);
      setEditData({});
      setRefreshKey(prev => prev + 1); // Trigger a refresh
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  // Handle changes to edited data
  const handleEditChange = (field: keyof TikTokComment, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Mark comment as replied
  const handleMarkReplied = async (comment: TikTokComment) => {
    try {
      await tiktokService.markTikTokCommentAsReplied(comment.id, !comment.is_replied);
      setRefreshKey(prev => prev + 1); // Trigger a refresh
    } catch (error) {
      console.error('Error updating reply status:', error);
    }
  };

  // Handle pagination change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Refresh data
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Count active filters
  const activeFiltersCount = Object.values(filters).filter(value => value !== '').length - (filters.search ? 1 : 0);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh',
      overflow: 'auto',
      bgcolor: theme.palette.background.default
    }}>
      <Box sx={{ 
        px: { xs: 2, sm: 3 }, 
        pt: 2,
        pb: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <Typography variant="h4" fontWeight="600" color="text.primary">
          TikTok Comments
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Manage and respond to comments from your TikTok videos
        </Typography>
      </Box>

      {/* Search and filter bar */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mx: { xs: 2, sm: 3 },
          mt: 2,
          mb: 2, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search comments..."
              variant="outlined"
              size="small"
              value={filters.search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5 }
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={toggleFilters}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start',
                borderRadius: 1.5,
                height: '40px'
              }}
            >
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              fullWidth
              sx={{ 
                borderRadius: 1.5,
                height: '40px',
                bgcolor: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                }
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>

        {/* Expandable filters */}
        {showFilters && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    name="priority"
                    value={filters.priority}
                    onChange={handleFilterChange}
                    label="Priority"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.priorities.map((priority) => (
                      <MenuItem key={priority} value={priority.toString()}>
                        {getPriorityLabel(priority)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sentiment</InputLabel>
                  <Select
                    name="sentiment"
                    value={filters.sentiment}
                    onChange={handleFilterChange}
                    label="Sentiment"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.sentiments.map((sentiment) => (
                      <MenuItem key={sentiment} value={sentiment}>
                        {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    label="Category"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Brand</InputLabel>
                  <Select
                    name="brand_id"
                    value={filters.brand_id}
                    onChange={handleFilterChange}
                    label="Brand"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.brands.map((brand) => (
                      <MenuItem key={brand.brand_id} value={brand.brand_id}>
                        {brand.brand_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Reply Status</InputLabel>
                  <Select
                    name="is_replied"
                    value={filters.is_replied}
                    onChange={handleFilterChange}
                    label="Reply Status"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Replied</MenuItem>
                    <MenuItem value="false">Not Replied</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Comments list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : comments.length === 0 ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 5, 
            mx: { xs: 2, sm: 3 },
            textAlign: 'center', 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper
          }}
        >
          <Typography variant="h6" color="text.secondary">No comments found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your filters or search criteria
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
          <Stack spacing={2}>
            {comments.map((comment) => (
              <Paper
                key={comment.id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                  }
                }}
              >
                <Box sx={{ 
                  p: 2, 
                  bgcolor: theme.palette.background.paper,
                  borderBottom: editingComment?.id === comment.id ? `1px solid ${theme.palette.divider}` : 'none'
                }}>
                  <Grid container spacing={2}>
                    {/* User info */}
                    <Grid item xs={12} display="flex" alignItems="center">
                      <Avatar 
                        src={comment.avatar_thumbnail || ''} 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 2,
                          border: `1px solid ${theme.palette.divider}`
                        }} 
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {comment.unique_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.created_at).toLocaleDateString()} • {new Date(comment.created_at).toLocaleTimeString()}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={comment.digg_count}
                          icon={<ThumbUpIcon fontSize="small" />}
                          sx={{ 
                            bgcolor: 'transparent',
                            border: `1px solid ${theme.palette.divider}`,
                            '& .MuiChip-icon': { color: theme.palette.text.secondary }
                          }}
                        />
                        <Chip
                          size="small"
                          label={comment.reply_comment_total}
                          icon={<CommentIcon fontSize="small" />}
                          sx={{ 
                            bgcolor: 'transparent',
                            border: `1px solid ${theme.palette.divider}`,
                            '& .MuiChip-icon': { color: theme.palette.text.secondary }
                          }}
                        />
                        <Tooltip title={comment.is_replied ? "Comment replied" : "Not replied yet"}>
                          <IconButton 
                            size="small"
                            color={comment.is_replied ? "success" : "default"}
                            onClick={() => handleMarkReplied(comment)}
                            sx={{ 
                              border: `1px solid ${comment.is_replied ? theme.palette.success.main : theme.palette.divider}`,
                              p: 1
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                    
                    {/* Video link */}
                    <Grid item xs={12}>
                      <Link 
                        href={comment.video_web_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ 
                          display: 'inline-block', 
                          mb: 1,
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          View TikTok Video
                        </Typography>
                      </Link>
                    </Grid>

                    {/* Comment text */}
                    <Grid item xs={12}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-word',
                          lineHeight: 1.6
                        }}
                      >
                        {convertUrlsToLinks(comment.text)}
                      </Typography>
                    </Grid>

                    {/* Tags */}
                    <Grid item xs={12} display="flex" flexWrap="wrap" gap={1}>
                      {comment.priority && (
                        <Chip
                          size="small"
                          label={getPriorityLabel(comment.priority)}
                          color={getPriorityColor(comment.priority)}
                          sx={{ borderRadius: '4px' }}
                        />
                      )}
                      {comment.sentiment && (
                        <Chip
                          size="small"
                          label={comment.sentiment.charAt(0).toUpperCase() + comment.sentiment.slice(1)}
                          color={getSentimentColor(comment.sentiment)}
                          sx={{ borderRadius: '4px' }}
                        />
                      )}
                      {comment.category && (
                        <Chip
                          size="small"
                          label={comment.category.charAt(0).toUpperCase() + comment.category.slice(1)}
                          sx={{ borderRadius: '4px' }}
                        />
                      )}
                      <Box sx={{ ml: 'auto' }}>
                        <IconButton 
                          size="small"
                          onClick={() => handleEditStart(comment)}
                          sx={{ 
                            border: `1px solid ${theme.palette.divider}`,
                            p: 1
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>

                    {/* Suggested reply (if exists) */}
                    {!editingComment && comment.suggested_reply && (
                      <Grid item xs={12}>
                        <Box 
                          sx={{ 
                            mt: 1,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                          }}
                        >
                          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                            Suggested Reply:
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              color: theme.palette.text.primary
                            }}
                          >
                            {comment.suggested_reply}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Edit mode */}
                {editingComment?.id === comment.id && (
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                            <InputLabel>Priority</InputLabel>
                            <Select
                              value={editData.priority?.toString() || ''}
                              onChange={(e) => handleEditChange('priority', parseInt(e.target.value))}
                              label="Priority"
                              sx={{ borderRadius: 1.5 }}
                            >
                              {filterOptions.priorities.map((priority) => (
                                <MenuItem key={priority} value={priority.toString()}>
                                  {getPriorityLabel(priority)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                            <InputLabel>Sentiment</InputLabel>
                            <Select
                              value={editData.sentiment || ''}
                              onChange={(e) => handleEditChange('sentiment', e.target.value)}
                              label="Sentiment"
                              sx={{ borderRadius: 1.5 }}
                            >
                              <MenuItem value="">None</MenuItem>
                              {filterOptions.sentiments.map((sentiment) => (
                                <MenuItem key={sentiment} value={sentiment}>
                                  {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={editData.category || ''}
                              onChange={(e) => handleEditChange('category', e.target.value)}
                              label="Category"
                              sx={{ borderRadius: 1.5 }}
                            >
                              <MenuItem value="">None</MenuItem>
                              {filterOptions.categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category.charAt(0).toUpperCase() + category.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Suggested Reply"
                          value={editData.suggested_reply || ''}
                          onChange={(e) => handleEditChange('suggested_reply', e.target.value)}
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleEditCancel}
                            sx={{ borderRadius: 1.5 }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleEditSave}
                            sx={{ borderRadius: 1.5 }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            ))}

            {/* Pagination */}
            {total > rowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <Pagination 
                  count={Math.ceil(total / rowsPerPage)} 
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="large"
                />
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default TikTokCommentsPage; 